import { atom, useAtom, type WritableAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useEffect, useRef, useState } from 'react';
import { usePreferences, useSetPreference } from '../data/hooks';

/**
 * Client state that is NOT server data lives in Jotai; server data lives in
 * TanStack Query (INV-13). The two never mirror each other.
 */

/**
 * APPEARANCE, IN APPLE'S THREE WORDS (Arif 2026-09-07, "theme per person").
 *
 * macOS System Settings offers Light, Dark and AUTOMATIC, and Automatic is the default.
 * "System" is what the web habitually calls the third state and it is not the word Apple
 * uses, so it appears nowhere here: the value, the type and the label all say automatic.
 *
 * Apple's own web app takes the fourth position - iCloud.com has no switch at all and
 * simply follows the device ("When you turn on Dark Mode on your Apple device, you also
 * turn on Dark Mode for iCloud.com"). The owner decided against that on 2026-09-07: this
 * product is not inside an ecosystem that decides for it, so it has to BE part of the
 * ecosystem, and the switch stays - built the way Apple builds the setting rather than
 * the way the web builds the toggle.
 */
export type ThemePreference = 'automatic' | 'light' | 'dark';

export const THEME_STORAGE_KEY = 'ofmos.theme';

/**
 * THE PREFERENCE KEY ON THE SERVER, in the per-user store `view-persistence.md` V1 uses
 * (`GET/PUT /api/auth/me/preferences`). `userPreferenceKeySchema` allows dotted
 * lower-case segments of letters and digits, each starting with a letter, so `appearance`
 * is a legal key as written.
 */
export const APPEARANCE_PREFERENCE_KEY = 'appearance';

/**
 * LOCAL STORAGE IS THE BOOT PATH AND STAYS THE BOOT PATH.
 *
 * The server is the RECORD of the choice and localStorage is the CACHE of it, in that
 * order, and the split is about one frame: the class on `<html>` has to be right before
 * the first paint, and a fetch cannot be. A product that waited for `/preferences` would
 * flash the light theme at every dark-mode user on every load, which is the single most
 * visible bug a theme switch can have. So the atom boots from storage, the server value
 * arrives a moment later and corrects it if the person changed their mind on another
 * machine, and a change writes both.
 */
export const themeAtom = atomWithStorage<ThemePreference>(THEME_STORAGE_KEY, 'automatic');

/**
 * A STORED VALUE FROM BEFORE THE RENAME. The third state was called `system` until
 * 2026-09-07 and is in every browser that has used this product; read as an unknown
 * string it would fall through every branch below and pin those people to light. One
 * line of translation is what that costs.
 */
export function normaliseThemePreference(value: unknown): ThemePreference | null {
  if (value === 'light' || value === 'dark' || value === 'automatic') return value;
  if (value === 'system') return 'automatic';
  return null;
}

/**
 * THE ICON STYLE, THE THEME COLOUR AND THE SIDEBAR SIZE (2026-09-09, the sidebar-symbols
 * package). Three more appearance settings that belong to the PERSON, built on exactly
 * the plumbing above and for exactly its reasons: an atom that boots out of storage so
 * nothing flashes, and a row on the server so the same person's next browser agrees.
 *
 * THEY ARE APPLE'S SETTINGS, IN APPLE'S WORDS. macOS 26 and iOS 26 let a person choose
 * how every app icon and settings tile is drawn - Default, Dark, Clear or Tinted - with
 * Always or Auto for the darkened ones, a Theme colour that Tinted is tinted with, and a
 * Sidebar icon size of Small, Medium or Large. Our sidebar tile IS one of those tiles
 * (it is the macOS kit's Service Icon), so it answers the same four questions.
 *
 * DEFAULTS ARE APPLE'S SHIPPING DEFAULTS, not the ones on any particular machine:
 * Default style, Auto, Multicolour, Medium.
 */
export type SymbolStyle = 'default' | 'dark' | 'clear' | 'tinted';

/**
 * THE SECOND HALF OF THE SETTING, AND IT IS NOT THE SAME PAIR FOR BOTH STYLES (SH-A5,
 * owner-13/14: "Wenn ich im Light Mode auf Clear klicke fuer die icons passiert nichts
 * Dark ebenfalls nichts Tinted ebenfalls nichts"). Apple's own pane gives each style the
 * modes that style can have:
 *
 *   "Dark: Items are opaque and have a darkened appearance. Choose Always to always show
 *   items in Dark style. Choose Auto to switch between Dark and Light, depending on system
 *   appearance."
 *   "Clear: Items are transparent. Choose Light for a light appearance, choose Dark for a
 *   darker appearance, or choose Auto to switch between Dark and Light, depending on
 *   system appearance."
 *   (support: https://support.apple.com/guide/mac-help/change-appearance-settings-mchlp1225/26/mac/26)
 *
 * So Dark carries `always | auto`, Clear carries `light | dark | auto`, and Default and
 * Tinted carry no mode at all. Until this round there was one pair for all four and one
 * rule behind it - "auto means only in the dark appearance" - which is TRUE OF DARK ONLY.
 * Clear and Tinted were therefore switched off in the light appearance, which is exactly
 * the "nothing happens" the owner found: the setting existed, the pane offered it, and the
 * light appearance always drew Default.
 */
export type SymbolStyleMode = 'always' | 'auto' | 'light' | 'dark';

/**
 * WHAT THE STYLESHEET SEES: one look per rule, with Clear's two variants spelled out, so
 * `index.css` never has to ask a second question about the appearance.
 */
export type ResolvedSymbolStyle = 'default' | 'dark' | 'clear-light' | 'clear-dark' | 'tinted';

/**
 * THE THEME COLOUR the Tinted style tints with. Apple's Appearance panel lists exactly
 * these nine, and `multicolour` is Apple's word for "no single colour": every tile keeps
 * its own, which is what Multicolour means for the accent too.
 */
export const SYMBOL_TINTS = [
  'multicolour',
  'blue',
  'purple',
  'pink',
  'red',
  'orange',
  'yellow',
  'green',
  'graphite',
] as const;

export type SymbolTint = (typeof SYMBOL_TINTS)[number];

/**
 * Two of Apple's three source-list sizes (macOS 27 kit: row 32 / 40). The kit's Small (row 24,
 * title 11) is removed (redesign register R-S4 and L7: nothing below 13px).
 */
export type SidebarSize = 'medium' | 'large';

/**
 * WHAT A SIDEBAR ROW LEADS WITH, and both ways ship (the owner's decision of 2026-09-16,
 * package A3 decision 2). `symbols` is the construction Apple's own sidebars use - hig:
 * https://developer.apple.com/design/human-interface-guidelines/sidebars, "By default,
 * sidebar icons use your app's accent color" - the same artwork, flat, in the accent, and it
 * is the DEFAULT since the owner's decision of 2026-10-04 (redesign register A5-2: "Mono"
 * becomes the default icon style, the tiles stay selectable). `tiles` is the coloured System
 * Settings tile this product has drawn since E9 and was the default until then.
 *
 * The choice does NOT touch the tile settings. Somebody who has picked Tinted and green for
 * their tiles and then switches to Symbols still has Tinted and green when they switch back:
 * the menu hides the groups that have nothing to act on, and hiding is not clearing.
 */
export type SidebarIcons = 'symbols' | 'tiles';

/**
 * THE DEFAULT LIVES HERE AND ONLY HERE: the atom's initial value and every fallback for an
 * unknown stored value read it, and the server holds a row only for a person who chose
 * (`useSyncPreference` with its storage key, below; migration 0031 cleared the rows that
 * recorded the old default as if somebody had picked it).
 */
export const DEFAULT_SIDEBAR_ICONS: SidebarIcons = 'symbols';

export const SYMBOL_STYLE_STORAGE_KEY = 'ofmos.symbol.style';
export const SYMBOL_STYLE_MODE_STORAGE_KEY = 'ofmos.symbol.style.mode';
export const SYMBOL_TINT_STORAGE_KEY = 'ofmos.symbol.tint';
export const SIDEBAR_SIZE_STORAGE_KEY = 'ofmos.sidebar.size';
export const SIDEBAR_ICONS_STORAGE_KEY = 'ofmos.sidebar.icons';

/** Dotted lower-case segments, which is what `userPreferenceKeySchema` allows. */
export const SYMBOL_STYLE_PREFERENCE_KEY = 'symbol.style';
export const SYMBOL_STYLE_MODE_PREFERENCE_KEY = 'symbol.style.mode';
export const SYMBOL_TINT_PREFERENCE_KEY = 'symbol.tint';
export const SIDEBAR_SIZE_PREFERENCE_KEY = 'sidebar.size';
export const SIDEBAR_ICONS_PREFERENCE_KEY = 'sidebar.icons';

export const symbolStyleAtom = atomWithStorage<SymbolStyle>(SYMBOL_STYLE_STORAGE_KEY, 'default');
export const symbolStyleModeAtom = atomWithStorage<SymbolStyleMode>(
  SYMBOL_STYLE_MODE_STORAGE_KEY,
  'auto',
);
export const symbolTintAtom = atomWithStorage<SymbolTint>(SYMBOL_TINT_STORAGE_KEY, 'multicolour');
export const sidebarSizeAtom = atomWithStorage<SidebarSize>(SIDEBAR_SIZE_STORAGE_KEY, 'large');
export const sidebarIconsAtom = atomWithStorage<SidebarIcons>(
  SIDEBAR_ICONS_STORAGE_KEY,
  DEFAULT_SIDEBAR_ICONS,
);

/**
 * A STORED OR SERVER VALUE IS A STRING OFF A WIRE OR OUT OF A BROWSER, so every one of
 * them is checked against the set before it reaches an attribute: an unknown value would
 * otherwise land on `<html>` and select nothing, which is a look no setting produces.
 */
export function normaliseSymbolStyle(value: unknown): SymbolStyle | null {
  return value === 'default' || value === 'dark' || value === 'clear' || value === 'tinted'
    ? value
    : null;
}

export function normaliseSymbolStyleMode(value: unknown): SymbolStyleMode | null {
  return value === 'always' || value === 'auto' || value === 'light' || value === 'dark'
    ? value
    : null;
}

/** Which modes a style offers, which is what the menu draws and what the resolver trusts. */
export const SYMBOL_STYLE_MODES: Record<SymbolStyle, readonly SymbolStyleMode[]> = {
  default: [],
  dark: ['always', 'auto'],
  clear: ['light', 'dark', 'auto'],
  tinted: [],
};

export function normaliseSymbolTint(value: unknown): SymbolTint | null {
  return SYMBOL_TINTS.find((tint) => tint === value) ?? null;
}

/** A stored Small, written before the size was removed (R-S4), reads as Medium. */
export function normaliseSidebarSize(value: unknown): SidebarSize | null {
  if (value === 'small') return 'medium';
  return value === 'medium' || value === 'large' ? value : null;
}

export function normaliseSidebarIcons(value: unknown): SidebarIcons | null {
  return value === 'symbols' || value === 'tiles' ? value : null;
}

/**
 * WHICH STYLE IS ACTUALLY DRAWN, and it is the only style the stylesheet ever sees.
 *
 * Apple's Auto is a rule about the APPEARANCE, not about the clock: a darkened icon
 * style applies in the dark appearance and the Default style applies in the light one.
 * Resolving it here rather than in CSS is what keeps the four looks in `index.css` free
 * of a second condition - a rule for `[data-symbol-style="dark"]` means the dark style
 * is on, and never "the dark style is on unless the appearance is light".
 */
export function resolveSymbolStyle(
  style: SymbolStyle,
  mode: SymbolStyleMode,
  appearance: 'light' | 'dark',
): ResolvedSymbolStyle {
  if (style === 'default' || style === 'tinted') return style;
  if (style === 'dark') {
    /**
     * Auto is Apple's "switch between Dark and Light": in the light appearance the Dark
     * style shows the LIGHT one, and the light one is what this product calls Default.
     * That is Apple's behaviour and not the defect - the defect was every OTHER style
     * doing the same thing.
     */
    return mode === 'auto' && appearance === 'light' ? 'default' : 'dark';
  }
  /**
   * Clear is visible in BOTH appearances and the person picks which of its two variants
   * they get; Auto follows the appearance. A stored `always` from before this round -
   * the value the old pair wrote for every style - reads as Auto, which is what Apple's
   * pane offers here and the closest thing to what that person had chosen.
   */
  const variant = mode === 'light' || mode === 'dark' ? mode : appearance;
  return variant === 'dark' ? 'clear-dark' : 'clear-light';
}

/**
 * There is no session atom. WHO is signed in is server state behind an httpOnly cookie, so
 * it lives in TanStack Query (`app/auth/useSession.ts`) like every other server fact -
 * INV-13, and the reason an expired session can redirect from anywhere.
 */

/** Command menu open state: one place, so cmd-k and the nav button agree. */
export const commandMenuOpenAtom = atom(false);

/**
 * Applies the theme to <html>. `dark` is the ONLY class this application puts
 * there: the design itself is the base token layer (ui/tokens.css), so there is
 * nothing to switch except light and dark. The tokens switch on a class, never on
 * a media query, so an explicit choice beats the OS (INV-14).
 *
 * Mounted by the shell and by every screen that renders outside it (kitchen
 * sink), because a route that forgets this renders in the OS theme.
 */
/**
 * WHICH THEME IS ACTUALLY ON SCREEN - 'light' or 'dark', never 'automatic'.
 *
 * A control that reports the PREFERENCE cannot tell the user what they are
 * looking at: under "automatic" it has to draw something that means "ask the OS",
 * and every glyph for that is a glyph for something else. This resolves the
 * preference against the OS so the control can show a sun or a moon and be
 * telling the truth in all three cases.
 */
export function useResolvedTheme(): 'light' | 'dark' {
  const [stored] = useAtom(themeAtom);
  const preference = normaliseThemePreference(stored) ?? 'automatic';
  const [systemDark, setSystemDark] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const read = () => setSystemDark(media.matches);
    read();
    media.addEventListener('change', read);
    return () => media.removeEventListener('change', read);
  }, []);

  if (preference !== 'automatic') {
    return preference;
  }
  return systemDark ? 'dark' : 'light';
}

/**
 * THE THREE ATTRIBUTES BESIDE THE CLASS (2026-09-09). `dark` stays the only CLASS this
 * application puts on `<html>`; the icon style, the theme colour and the sidebar size are
 * ATTRIBUTES, because each of them is one value out of a set rather than a switch, and an
 * attribute is what a stylesheet can select on by value without four classes per setting.
 *
 * THE STYLE IS WRITTEN RESOLVED. `data-symbol-style` carries what is DRAWN, never the
 * preference: with Auto and the light appearance that is `default`, whatever was chosen.
 * The listener below is what makes it follow the OS live, exactly as the class does.
 */
export function useApplyTheme(): void {
  const [stored] = useAtom(themeAtom);
  const preference = normaliseThemePreference(stored) ?? 'automatic';
  const [storedStyle] = useAtom(symbolStyleAtom);
  const [storedMode] = useAtom(symbolStyleModeAtom);
  const [storedTint] = useAtom(symbolTintAtom);
  const [storedSize] = useAtom(sidebarSizeAtom);
  const [storedIcons] = useAtom(sidebarIconsAtom);
  const style = normaliseSymbolStyle(storedStyle) ?? 'default';
  const mode = normaliseSymbolStyleMode(storedMode) ?? 'auto';
  const tint = normaliseSymbolTint(storedTint) ?? 'multicolour';
  const size = normaliseSidebarSize(storedSize) ?? 'large';
  /* An unknown value is not a third construction: it reads as the default (decision 4). */
  const icons = normaliseSidebarIcons(storedIcons) ?? DEFAULT_SIDEBAR_ICONS;

  useEffect(() => {
    const root = document.documentElement;
    const media =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-color-scheme: dark)')
        : null;

    const apply = () => {
      const dark =
        preference === 'dark' || (preference === 'automatic' && (media?.matches ?? false));
      root.classList.toggle('dark', dark);
      root.setAttribute(
        'data-symbol-style',
        resolveSymbolStyle(style, mode, dark ? 'dark' : 'light'),
      );
      root.setAttribute('data-symbol-tint', tint);
      root.setAttribute('data-sidebar-size', size);
      root.setAttribute('data-sidebar-icons', icons);
    };

    apply();
    if (preference !== 'automatic' || media === null) {
      return;
    }
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [preference, style, mode, tint, size, icons]);
}

/**
 * READING AND WRITING ONE APPEARANCE PREFERENCE, per person (Arif 2026-09-07). It was
 * written for the appearance alone and generalised on 2026-09-09, when the icon style,
 * the theme colour and the sidebar size joined it: the argument below is about a VALUE
 * that belongs to a person and boots out of storage, and it is the same argument four
 * more times, so it is one function with the key and the atom handed in.
 *
 * WHAT THIS IS FOR: the same person on a second browser, a second device or after
 * clearing their browser gets the appearance they chose, and a different person on the
 * same machine gets their own. `localStorage` alone can promise neither - it is a
 * property of a BROWSER PROFILE, not of an account, and this product has five to fifteen
 * people who share machines. It is the same argument `view-persistence.md` V4 makes about
 * the last view, and it lands in the same store for the same reason.
 *
 * THE SERVER WINS ON ARRIVAL, EXACTLY ONCE. When `/preferences` answers with a value that
 * differs from what booted out of storage, the atom is corrected and storage is rewritten
 * (the atom writes it). ONCE, guarded by a ref, because after that first reconciliation
 * this browser's own choice is the newer fact: without the guard a change here would be
 * undone on the next render by a query result the mutation has already superseded.
 *
 * A WRITE FAILURE IS NOT AN ERROR ANYBODY IS SHOWN. The theme has already changed on
 * screen and in storage, so the person got what they asked for on this machine; what they
 * lost is the sync, and a toast over a working screen would be noise. `useSetPreference`
 * rolls its own cache entry back on failure, which is where a later reader would see it.
 */
function useSyncPreference<T>(
  key: string,
  preferenceAtom: WritableAtom<T, [T], void>,
  normalise: (value: unknown) => T | null,
  /**
   * GIVEN, the write-up below sends only a value THIS BROWSER holds under that storage key,
   * never the atom's initial value (redesign register A5-2, 2026-10-04). Without it a person
   * who never chose got the default written as their row on first sight, and a later default
   * could not reach them: every row since the A3 deploy read `tiles` like a choice. Only the
   * sidebar icons pass it; the other four keys keep the old write-up.
   */
  storageKey?: string,
): void {
  const [stored, setStored] = useAtom(preferenceAtom);
  const preferences = usePreferences();
  const setPreference = useSetPreference();
  const adopted = useRef(false);

  useEffect(() => {
    if (adopted.current || preferences.data === undefined) return;
    adopted.current = true;
    const remote = normalise(preferences.data[key]);

    /**
     * NO ROW YET MEANS THIS BROWSER'S VALUE IS THE ONE TO KEEP, and it has to be WRITTEN
     * rather than merely used - which is the half that was missing until 2026-09-08.
     *
     * The reconciliation below only ever read. So a person who had picked Dark before this
     * feature existed, or who picks it and never opens a second browser, had a choice that
     * lived in `localStorage` alone: their next machine opened in Automatic, and - the case
     * that actually bites on this instance - the NEXT PERSON to sign in on a shared machine
     * inherited it, because storage belongs to the browser profile and not to the account.
     * Writing the stored value up on first sight makes the row exist for exactly the person
     * whose browser it came from, after which the ordinary path owns it.
     *
     * It is a WRITE and not a merge: there is nothing to merge with. The mutation is
     * optimistic and its failure is not shown, for the reason above.
     */
    if (remote === null) {
      if (storageKey === undefined) {
        setPreference.mutate({ key, value: stored });
        return;
      }
      const own = normalise(readStoredValue(storageKey));
      if (own !== null) setPreference.mutate({ key, value: own });
      return;
    }

    if (remote === stored) return;
    setStored(remote);
  }, [key, normalise, preferences.data, stored, setStored, setPreference, storageKey]);
}

/**
 * ONE HOOK FOR THE WHOLE APPEARANCE PANE, which is four preferences since 2026-09-09:
 * the appearance itself, the icon style with its Always/Auto, the theme colour and the
 * sidebar icon size. Each is reconciled independently and on its own guard - a person
 * who changed the icon style on this machine and the appearance on another gets both,
 * and neither adoption can undo the other.
 */
export function useSyncAppearance(): void {
  useSyncPreference(APPEARANCE_PREFERENCE_KEY, themeAtom, normaliseThemePreference);
  useSyncPreference(SYMBOL_STYLE_PREFERENCE_KEY, symbolStyleAtom, normaliseSymbolStyle);
  useSyncPreference(
    SYMBOL_STYLE_MODE_PREFERENCE_KEY,
    symbolStyleModeAtom,
    normaliseSymbolStyleMode,
  );
  useSyncPreference(SYMBOL_TINT_PREFERENCE_KEY, symbolTintAtom, normaliseSymbolTint);
  useSyncPreference(SIDEBAR_SIZE_PREFERENCE_KEY, sidebarSizeAtom, normaliseSidebarSize);
  useSyncPreference(
    SIDEBAR_ICONS_PREFERENCE_KEY,
    sidebarIconsAtom,
    normaliseSidebarIcons,
    SIDEBAR_ICONS_STORAGE_KEY,
  );
}

/**
 * What this browser holds under a storage key, parsed the way `atomWithStorage` wrote it, or
 * null when it holds nothing or something unreadable (a blocked or private storage throws).
 */
function readStoredValue(storageKey: string): unknown {
  try {
    const raw = window.localStorage.getItem(storageKey);
    return raw === null ? null : JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * The setter every appearance control uses. It writes the atom (which writes storage, so
 * the next boot has no flash) and the server (so the next browser has the same answer).
 */
function useSetPreferenceAtom<T>(
  key: string,
  preferenceAtom: WritableAtom<T, [T], void>,
): (value: T) => void {
  const [, setStored] = useAtom(preferenceAtom);
  const setPreference = useSetPreference();

  return (value: T) => {
    setStored(value);
    setPreference.mutate({ key, value });
  };
}

export function useSetAppearance(): (preference: ThemePreference) => void {
  return useSetPreferenceAtom(APPEARANCE_PREFERENCE_KEY, themeAtom);
}

export function useSetSymbolStyle(): (style: SymbolStyle) => void {
  return useSetPreferenceAtom(SYMBOL_STYLE_PREFERENCE_KEY, symbolStyleAtom);
}

export function useSetSymbolStyleMode(): (mode: SymbolStyleMode) => void {
  return useSetPreferenceAtom(SYMBOL_STYLE_MODE_PREFERENCE_KEY, symbolStyleModeAtom);
}

export function useSetSymbolTint(): (tint: SymbolTint) => void {
  return useSetPreferenceAtom(SYMBOL_TINT_PREFERENCE_KEY, symbolTintAtom);
}

export function useSetSidebarSize(): (size: SidebarSize) => void {
  return useSetPreferenceAtom(SIDEBAR_SIZE_PREFERENCE_KEY, sidebarSizeAtom);
}

export function useSetSidebarIcons(): (icons: SidebarIcons) => void {
  return useSetPreferenceAtom(SIDEBAR_ICONS_PREFERENCE_KEY, sidebarIconsAtom);
}

/** The construction a sidebar row draws right now, normalised, for the components. */
export function useSidebarIcons(): SidebarIcons {
  const [stored] = useAtom(sidebarIconsAtom);
  return normaliseSidebarIcons(stored) ?? DEFAULT_SIDEBAR_ICONS;
}
